import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, Edit, Trash2, Plus, Filter, Phone, CheckCircle, Users, FolderOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import LeadModal from '../components/LeadModal';
import SecretaryManagementModal from '../components/secretary/SecretaryManagementModal';
import LeadHandlingModal from '../components/LeadHandlingModal';
import ClientDataViewerModal from '../components/leads/ClientDataViewerModal';
import ImportLeadsModal from '../components/ImportLeadsModal';
import ProtectedContent from '../components/ProtectedContent';
import IconButton from '../components/common/IconButton';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import StatusFilterTabs from '../components/leads/StatusFilterTabs';
import RoleIndicatorChip from '../components/leads/RoleIndicatorChip';
import FilterDrawer from '../components/leads/FilterDrawer';
import StatusCellWithTooltip from '../components/clients/StatusCellWithTooltip';
import { UserRole } from '../utils/authUtils';
import { contractApi } from '../services/contractService';
import {
  LeadDto,
  LeadFilterDto,
  leadsApi,
  handleApiError,
  formatDate,
  BulkAssignmentDto,
  BulkDeleteDto,
  getBenefitTypeName
} from '../services/leadsService';
import { usersApi, UserDto } from '../services/usersService';
import {
  statusesApi,
  leadSourcesApi,
  StatusDto,
  LeadSourceDto
} from '../services/settingsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AdvancedFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const BulkActions = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 12px 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const BulkInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const BulkButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BulkSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const BulkButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 8px 14px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : props.variant === 'danger' ? `
    background: transparent;
    color: var(--secondary-color);
    border: 1px solid var(--secondary-color);
    &:hover:not(:disabled) {
      color: var(--error-color);
      border-color: var(--error-color);
    }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusText = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
  filter: saturate(0.7) brightness(0.85);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const LeadsPage: React.FC = () => {
  const { user, refreshSecretaryAssignments } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);

  // Pagination - read from URL
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize');
    return size ? parseInt(size) : 25;
  });
  const [totalCount, setTotalCount] = useState(0);

  // Filters - read from URL
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const statuses = searchParams.get('statuses');
    return statuses ? statuses.split(',') : [];
  });
  const [salesRepFilter, setSalesRepFilter] = useState<string>(() => searchParams.get('salesRep') || '');
  const [sourceFilter, setSourceFilter] = useState<string>(() => searchParams.get('source') || '');
  const [dateFromFilter, setDateFromFilter] = useState(() => searchParams.get('dateFrom') || '');
  const [dateToFilter, setDateToFilter] = useState(() => searchParams.get('dateTo') || '');

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 25) params.set('pageSize', pageSize.toString());
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter.length > 0) params.set('statuses', statusFilter.join(','));
    if (salesRepFilter) params.set('salesRep', salesRepFilter);
    if (sourceFilter) params.set('source', sourceFilter);
    if (dateFromFilter) params.set('dateFrom', dateFromFilter);
    if (dateToFilter) params.set('dateTo', dateToFilter);

    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchTerm, statusFilter, salesRepFilter, sourceFilter, dateFromFilter, dateToFilter, setSearchParams]);
  
  // Bulk actions
  const [bulkAssignSalesRep, setBulkAssignSalesRep] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Single lead delete
  const [deleteConfirmLead, setDeleteConfirmLead] = useState<{ id: number; name: string; hasContract: boolean } | null>(null);

  // Modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHandlingModal, setShowHandlingModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [editingLead, setEditingLead] = useState<LeadDto | null>(null);
  const [handlingLead, setHandlingLead] = useState<LeadDto | null>(null);
  const [permissionsLead, setPermissionsLead] = useState<LeadDto | null>(null);
  const [clientDataLead, setClientDataLead] = useState<LeadDto | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  
  // Contract status cache for leads
  const [contractStatuses, setContractStatuses] = useState<Record<number, string>>({});

  // Determine user permissions
  const isSystemAdmin = user?.permissionLevel === 1;
  const isSecretary = user?.permissionLevel === 4;
  const isSupervisor = user?.isSupervisor;
  const isPartialSupervisor = user?.isPartialSupervisor;
  // מפקח חלקי רואה את לידי נציגיו (השרת מצמצם לפי SupervisedSalesRepIds) — לא לכפות assignedSalesRepId=עצמו
  const showAllLeads = isSystemAdmin || isSupervisor || isPartialSupervisor;

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams: LeadFilterDto = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || undefined,
        statusIds: statusFilter.length > 0 ? statusFilter.map(id => parseInt(id)) : undefined,
        // הסינון על Secretary נעשה בשרת לפי SecretaryLeads (לא assignedSalesRepId)
        // עבור SalesRep רגיל — מסונן לפי הנציג שלו
        // עבור Admin/Supervisor — לפי הפילטר שבחרו
        assignedSalesRepId: !showAllLeads && !isSecretary
          ? user?.id
          : salesRepFilter ? (salesRepFilter === '-1' ? -1 : parseInt(salesRepFilter)) : undefined,
        leadSourceIds: sourceFilter ? [parseInt(sourceFilter)] : undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
      };

      const result = await leadsApi.getAll(filterParams);
      setLeads(result.leads);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, salesRepFilter, sourceFilter, dateFromFilter, dateToFilter, user, showAllLeads, isSecretary]);

  // Load contract statuses for current leads
  const loadContractStatuses = useCallback(async (leadIds: number[]) => {
    try {
      const contractPromises = leadIds.map(async (leadId) => {
        try {
          const contract = await contractApi.getByLeadId(leadId);
          return contract ? { leadId, status: contract.status } : { leadId, status: null };
        } catch {
          return { leadId, status: null };
        }
      });

      const results = await Promise.all(contractPromises);
      const statusMap: Record<number, string> = {};

      results.forEach(({ leadId, status }) => {
        if (status) {
          statusMap[leadId] = status;
        }
      });

      setContractStatuses(statusMap);
    } catch (error) {
      console.error('Error loading contract statuses:', error);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      const promises: Promise<any>[] = [
        statusesApi.search('', 1), // Only load lead statuses (group 1)
        leadSourcesApi.getAll()
      ];
      
      // Only load sales reps data if user is admin
      if (isSystemAdmin) {
        promises.push(usersApi.getSalesReps());
      }
      
      const results = await Promise.all(promises);
      
      setStatuses(results[0]);
      setLeadSources(results[1]);
      if (isSystemAdmin) {
        setUsers(results[2]);
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  }, [isSystemAdmin]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // עבור מזכירה: לרענן את רשימת ההקצאות שלה בכניסה לעמוד — לוודא טריות (admin עלול לשנות הקצאות)
  useEffect(() => {
    if (isSecretary) {
      refreshSecretaryAssignments();
    }
  }, [isSecretary, refreshSecretaryAssignments]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Load contract statuses when leads change
  useEffect(() => {
    if (leads.length > 0) {
      const leadIds = leads.map(lead => lead.id);
      loadContractStatuses(leadIds);
    }
  }, [leads, loadContractStatuses]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setSalesRepFilter('');
    setSourceFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setCurrentPage(1);
  };

  const handleAdvancedFilter = (filters: {
    sourceFilter: string;
    salesRepFilter: string;
    dateFrom: string;
    dateTo: string;
  }) => {
    setSourceFilter(filters.sourceFilter);
    setSalesRepFilter(filters.salesRepFilter);
    setDateFromFilter(filters.dateFrom);
    setDateToFilter(filters.dateTo);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleSelectLead = (leadId: number) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignSalesRep || selectedLeads.length === 0) return;

    try {
      const assignmentData: BulkAssignmentDto = {
        leadIds: selectedLeads,
        salesRepId: parseInt(bulkAssignSalesRep)
      };

      const result = await leadsApi.bulkAssign(assignmentData);
      
      if (result.success) {
        setSelectedLeads([]);
        setBulkAssignSalesRep('');
        loadLeads();
      } else {
        setError(`הקצאה נכשלה: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;

    try {
      const deleteData: BulkDeleteDto = {
        leadIds: selectedLeads
      };

      await leadsApi.bulkDelete(deleteData);
      setSelectedLeads([]);
      setShowDeleteConfirm(false);
      loadLeads();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleAddLead = () => {
    setEditingLead(null);
    setModalMode('create');
    setShowLeadModal(true);
  };

  const handleEditLead = (lead: LeadDto) => {
    setEditingLead(lead);
    setModalMode('edit');
    setShowLeadModal(true);
  };

  const handleViewLead = (lead: LeadDto) => {
    setEditingLead(lead);
    setModalMode('view');
    setShowLeadModal(true);
  };

  const handleLead = (lead: LeadDto) => {
    setHandlingLead(lead);
    setShowHandlingModal(true);
  };

  const handleDeleteLead = (lead: LeadDto) => {
    // Check if lead has a contract
    const hasContract = !!contractStatuses[lead.id];

    setDeleteConfirmLead({
      id: lead.id,
      name: lead.fullName,
      hasContract
    });
  };

  const handleConfirmDeleteLead = async () => {
    if (!deleteConfirmLead) return;

    try {
      await leadsApi.delete(deleteConfirmLead.id);
      setDeleteConfirmLead(null);
      loadLeads();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirmLead(null);
    }
  };

  const handleLeadSaved = (savedLead: LeadDto) => {
    loadLeads();
    setShowLeadModal(false);
    setEditingLead(null);
    setModalMode('create');
  };

  const handleImported = () => {
    loadLeads();
  };

  // Dynamic page title based on user role
  const getPageTitle = () => {
    if (isSystemAdmin) return 'ניהול לידים';
    if (isSupervisor) return 'לידים (מפקח)';
    if (isPartialSupervisor) return 'לידים (מפקח חלקי)';
    return 'הלידים שלי';
  };

  // Info message based on user role
  const columns = [
    // Checkbox column - only for admins
    ...(isSystemAdmin ? [{
      key: 'select',
      title: 'בחירה',
      width: '60px',
      render: (_: any, record: LeadDto) => (
        <input
          type="checkbox"
          checked={selectedLeads.includes(record.id)}
          onChange={() => handleSelectLead(record.id)}
        />
      )
    }] : []),
    {
      key: 'fullName',
      title: 'שם מלא',
      width: '200px'
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '150px'
    },
    {
      key: 'leadSourceName',
      title: 'מקור',
      width: '120px'
    },
    {
      key: 'benefitType',
      title: 'סוג קצבה',
      width: '150px',
      render: (value: number) => getBenefitTypeName(value)
    },
    {
      key: 'statusName',
      title: 'סטטוס',
      width: '150px',
      render: (value: string, record: LeadDto) => {
        // עבור ליד שחתם — אם השרת החזיר סטטוס לקוח (נציג מכירות + פרמטר מופעל),
        // מציגים את סטטוס הלקוח בדיוק כמו בטבלת הלקוחות (כולל ריבוי סטטוסים + hover)
        if (record.currentStatusName) {
          return (
            <StatusCellWithTooltip
              currentStatusName={record.currentStatusName}
              currentStatusColor={record.currentStatusColor}
              currentStages={record.currentStages}
            />
          );
        }

        const contractStatus = contractStatuses[record.id];
        const isFullySigned = contractStatus?.toLowerCase() === 'fullysigned';

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <StatusText color={record.statusColor}>
              {value}
            </StatusText>
            {isFullySigned && (
              <CheckCircle size={14} color="var(--success-color)" />
            )}
          </div>
        );
      }
    },
    // Sales rep column - only for admins and supervisors
    ...(showAllLeads ? [{
      key: 'assignedSalesRepName',
      title: 'נציג מכירות',
      width: '150px',
      render: (value: string) => value || 'לא מוקצה'
    }] : []),
    {
      key: 'createdAt',
      title: 'תאריך יצירה',
      width: '140px',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'lastUpdatedAt',
      title: 'עודכן לאחרונה',
      width: '140px',
      render: (value: string) => value ? formatDate(value) : '-'
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '140px',
      render: (_: any, record: LeadDto) => (
        <ActionsCell>
          {user?.permissionLevel === 2 && (
            <IconButton
              icon={Phone}
              onClick={(e) => { e.stopPropagation(); handleLead(record); }}
              title="טפל בליד"
              variant="primary"
            />
          )}
          <IconButton
            icon={Eye}
            onClick={(e) => { e.stopPropagation(); handleViewLead(record); }}
            title="צפייה בליד"
          />
          {record.clientId && (
            <IconButton
              icon={FolderOpen}
              onClick={(e) => { e.stopPropagation(); setClientDataLead(record); }}
              title="צפייה בנתוני לקוח"
            />
          )}
          <IconButton
            icon={Edit}
            onClick={(e) => { e.stopPropagation(); handleEditLead(record); }}
            title="עריכת ליד"
          />
          {(isSystemAdmin || user?.permissionLevel === 2 || isSecretary) && (
            <IconButton
              icon={Users}
              onClick={(e) => { e.stopPropagation(); setPermissionsLead(record); }}
              title="ניהול הרשאות מזכירות"
            />
          )}
          {isSystemAdmin && (
            <IconButton
              icon={Trash2}
              onClick={(e) => { e.stopPropagation(); handleDeleteLead(record); }}
              title="מחיקת ליד"
              variant="danger"
            />
          )}
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>{getPageTitle()}</PageTitle>
          <RoleIndicatorChip
            permissionLevel={user?.permissionLevel}
            isSupervisor={isSupervisor}
          />
        </HeaderRight>
        <HeaderActions>
          <ProtectedContent requiredRoles={[UserRole.SYSTEM_ADMIN, UserRole.SECRETARY]}>
            <Button variant="primary" onClick={handleAddLead}>
              <Plus size={16} style={{ marginLeft: '6px', verticalAlign: 'middle' }} />
              ליד חדש
            </Button>
          </ProtectedContent>
        </HeaderActions>
      </PageHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="חיפוש לפי שם, טלפון או דוא״ל..."
        />

        <FilterRow>
          <StatusFilterTabs
            statuses={statuses}
            selectedStatuses={statusFilter}
            onChange={(values) => {
              setStatusFilter(values);
              setCurrentPage(1);
            }}
          />
          <AdvancedFilterButton onClick={() => setShowFilterDrawer(true)}>
            <Filter size={16} />
            מסננים מתקדמים
          </AdvancedFilterButton>
        </FilterRow>
      </FilterContainer>

      <FilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApply={handleAdvancedFilter}
        onClear={handleClearFilters}
        filters={{
          sourceFilter,
          salesRepFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter
        }}
        leadSources={leadSources}
        salesReps={users}
        showSalesRepFilter={isSystemAdmin}
      />

      {isSystemAdmin && selectedLeads.length > 0 && (
        <BulkActions>
          <BulkInfo>
            נבחרו {selectedLeads.length} לידים
          </BulkInfo>
          <BulkButtons>
            <BulkSelect
              value={bulkAssignSalesRep}
              onChange={(e) => setBulkAssignSalesRep(e.target.value)}
            >
              <option value="">בחר נציג מכירות</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.fullName}
                </option>
              ))}
            </BulkSelect>
            <BulkButton
              variant="primary"
              onClick={handleBulkAssign}
              disabled={!bulkAssignSalesRep}
            >
              הקצה
            </BulkButton>
            <BulkButton
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              מחק נבחרים
            </BulkButton>
          </BulkButtons>
        </BulkActions>
      )}

      <DataTable
        data={leads}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: handlePageChange
        }}
        actions={
          isSystemAdmin ? (
            <input
              type="checkbox"
              checked={selectedLeads.length === leads.length && leads.length > 0}
              onChange={handleSelectAll}
              title="בחר הכל"
            />
          ) : undefined
        }
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="אישור מחיקה"
        message={`האם אתה בטוח שברצונך למחוק ${selectedLeads.length} לידים? פעולה זו לא ניתנת לביטול.`}
        confirmText="מחק"
        cancelText="ביטול"
      />

      {/* Single lead delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmLead}
        onClose={() => setDeleteConfirmLead(null)}
        onConfirm={handleConfirmDeleteLead}
        title="אישור מחיקת ליד"
        message={
          deleteConfirmLead?.hasContract
            ? `⚠️ אזהרה: ליד "${deleteConfirmLead.name}" כבר הפך ללקוח בטיפול.\n\nמחיקת הליד תנתק את הקישור בין הליד לתיק הלקוח, אך תיק הלקוח (כולל מסמכים, פגישות ותשלומים) ימשיך להתקיים במערכת.\n\nהאם אתה בטוח שברצונך להמשיך?`
            : `האם אתה בטוח שברצונך למחוק את הליד "${deleteConfirmLead?.name}"?`
        }
        confirmText="מחק ליד"
        cancelText="ביטול"
        type={deleteConfirmLead?.hasContract ? "danger" : "info"}
      />

      <LeadModal
        isOpen={showLeadModal}
        onClose={() => {
          setShowLeadModal(false);
          setEditingLead(null);
          setModalMode('create');
        }}
        onSave={handleLeadSaved}
        lead={editingLead}
        mode={modalMode}
      />

      <ProtectedContent requiredRoles={[UserRole.SYSTEM_ADMIN]}>
        <ImportLeadsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImported={handleImported}
        />
      </ProtectedContent>

      {handlingLead && (
        <LeadHandlingModal
          isOpen={showHandlingModal}
          onClose={() => {
            setShowHandlingModal(false);
            setHandlingLead(null);
          }}
          lead={handlingLead}
          onLeadUpdated={loadLeads}
        />
      )}

      {permissionsLead && (
        <SecretaryManagementModal
          isOpen={!!permissionsLead}
          onClose={() => setPermissionsLead(null)}
          entityType="lead"
          entityId={permissionsLead.id}
        />
      )}

      {clientDataLead && (
        <ClientDataViewerModal
          isOpen={!!clientDataLead}
          onClose={() => setClientDataLead(null)}
          lead={clientDataLead}
        />
      )}
    </PageContainer>
  );
};

export default LeadsPage;